import React, { useState, useEffect, useMemo } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getCurrencyHistory } from "../../../common/services/markets/currencyService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const CurrencyHistory = ({ currencyId }) => {
  const { t } = useTranslation(["markets"]);
  const [currencyHistory, setCurrencyHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  // Fetch currency history on component mount
  useEffect(() => {
    const fetchHistory = async () => {
      const data = await getCurrencyHistory(currencyId);
      const formattedData = mapToViewModel(data);
      setCurrencyHistory(_.orderBy(formattedData, "id", "desc"));
      setIsLoading(false);
    };

    fetchHistory();
  }, [currencyId]);

  // Map API data to view model with formatted dates
  const mapToViewModel = (currencies) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return currencies.map((currency) => ({
      id: currency.id,
      name: currency.name,
      symbol: currency.symbol,
      active: currency.active,
      updatedByUserAccountId: currency.createdByUserAccountId,
      updatedBy:
        currency.updatedByUserAccountFirstName +
        " " +
        currency.updatedByUserAccountLastName,
      updatedDate: new Date(currency.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(currency.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: currency.eventId,
    }));
  };

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("RecordID"),
        sortable: true,
        filter: true,
        sort: "desc",
      },
      {
        field: "name",
        headerName: t("CurrencyName"),
        sortable: true,
        filter: true,
      },
      {
        field: "symbol",
        headerName: t("CurrencySymbol"),
        sortable: true,
        filter: true,
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      flex: 1,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("CurrencyID")} {currencyId}
      </div>
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={currencyHistory}
        getRowId={(params) => params.data.id.toString()}
        // Use normal layout instead of autoHeight for pagination
        domLayout="autoHeight"
        animateRows={true}
        // Enable pagination
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={paginationConfig.pageSizeOptions}
        onPaginationChanged={(event) => {
          const newPageSize = event.api.paginationGetPageSize();
          if (newPageSize !== pageSize) {
            handlePageSizeChange(newPageSize);
          }
        }}
        suppressPaginationPanel={false}
        height="400px"
      />
    </div>
  );
};

export default CurrencyHistory;

